import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Container, Link, Stack,
  TextField, Typography,
} from '@mui/material';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth';
import type { AcceptInviteResponse } from '../api/types';

/**
 * Self-service signup. Mirrors the LoginPage layout and the AcceptInvitePage
 * auto-login behaviour — server creates the User and returns a token + session
 * payload, we drop the user straight onto /home.
 */
export function SignupPage() {
  const setSession = useAuthStore((s) => s.setSession);
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (token) {
    navigate('/home', { replace: true });
    return null;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post<AcceptInviteResponse>('/api/auth/signup', { username, email, password });
      if (data.autoLogin && data.token && data.user) {
        setSession(data.token, data.user);
        navigate('/home', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    } catch (ex: unknown) {
      const msg = (ex as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Signup failed';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
      <Container maxWidth="xs">
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>Create account</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Pick a username, email, and password — we'll log you straight in.
            </Typography>
            <Stack component="form" onSubmit={onSubmit} spacing={2}>
              <TextField
                label="Username" value={username} onChange={(e) => setUsername(e.target.value)}
                autoFocus required size="small" autoComplete="username"
              />
              <TextField
                label="Email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required size="small"
                autoComplete="email"
              />
              <TextField
                label="Password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required size="small"
                autoComplete="new-password" helperText="At least 8 characters."
              />
              <TextField
                label="Confirm password" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required size="small"
                autoComplete="new-password"
              />
              {err && <Alert severity="error" variant="outlined">{err}</Alert>}
              <Button type="submit" variant="contained" disabled={busy} size="large"
                      startIcon={busy ? <CircularProgress size={18} /> : null}>
                {busy ? 'Creating…' : 'Create account'}
              </Button>
              <Typography variant="body2" align="center" color="text.secondary">
                Already have an account? <Link component={RouterLink} to="/login">Sign in</Link>
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
